// src/utils/thermalDesign.ts
// Thermal bill design settings (per-field font size / bold / show-hide), saved per computer.

export type ThermalFieldKey =
  | 'logo'
  | 'companyName'
  | 'companyAddress'
  | 'companyContact'
  | 'title'
  | 'billInfo'
  | 'customer'
  | 'itemName'
  | 'itemDetails'
  | 'itemQtyAmount'
  | 'totals'
  | 'grandTotal'
  | 'paymentDetails'
  | 'amountWords'
  | 'qrLabel'
  | 'terms'
  | 'footer'
  | 'generatedLine';

export interface ThermalField {
  show: boolean;
  size: number; // px
  bold: boolean;
}

export interface ThermalDesign {
  fields: Record<ThermalFieldKey, ThermalField>;
  titleText: string;
  lineHeight: number; // 1.2 - 1.6
  logoHeightMm: number;
  qrSizeMm: number;
  scale58: number; // % applied to font sizes on small rolls (< 70mm)
  sideMarginMm: number; // left/right blank space on the roll
}

export const THERMAL_FIELDS: {
  key: ThermalFieldKey;
  label: string;
  canHide: boolean;
  sizeable: boolean;
}[] = [
  { key: 'logo', label: 'Logo', canHide: true, sizeable: false },
  { key: 'companyName', label: 'Company Name', canHide: true, sizeable: true },
  { key: 'companyAddress', label: 'Company Address', canHide: true, sizeable: true },
  { key: 'companyContact', label: 'GSTIN / Phone / Email', canHide: true, sizeable: true },
  { key: 'title', label: 'Bill Title', canHide: true, sizeable: true },
  { key: 'billInfo', label: 'Bill No / Date / Payment', canHide: false, sizeable: true },
  { key: 'customer', label: 'Customer (Billing To)', canHide: true, sizeable: true },
  { key: 'itemName', label: 'Item Name', canHide: false, sizeable: true },
  { key: 'itemDetails', label: 'Item Rate / GST / HSN', canHide: true, sizeable: true },
  { key: 'itemQtyAmount', label: 'Qty & Amount', canHide: false, sizeable: true },
  { key: 'totals', label: 'Totals (Subtotal, Tax…)', canHide: false, sizeable: true },
  { key: 'grandTotal', label: 'Grand Total', canHide: false, sizeable: true },
  { key: 'paymentDetails', label: 'Payment Details', canHide: true, sizeable: true },
  { key: 'amountWords', label: 'Amount in Words', canHide: true, sizeable: true },
  { key: 'qrLabel', label: 'QR Label & UPI ID', canHide: true, sizeable: true },
  { key: 'terms', label: 'Terms & Conditions', canHide: true, sizeable: true },
  { key: 'footer', label: 'Footer Text', canHide: true, sizeable: true },
  { key: 'generatedLine', label: '"Computer generated" line', canHide: true, sizeable: true },
];

const f = (size: number, bold = false, show = true): ThermalField => ({ show, size, bold });

export const DEFAULT_THERMAL_DESIGN: ThermalDesign = {
  fields: {
    logo: f(0, false),
    companyName: f(16, true),
    companyAddress: f(10.5),
    companyContact: f(10.5),
    title: f(12, true),
    billInfo: f(11),
    customer: f(11),
    itemName: f(11, true),
    itemDetails: f(10),
    itemQtyAmount: f(11),
    totals: f(11),
    grandTotal: f(14, true),
    paymentDetails: f(11),
    amountWords: f(10.5),
    qrLabel: f(11, true),
    terms: f(9.5),
    footer: f(10.5, true),
    generatedLine: f(9.5),
  },
  titleText: 'TAX INVOICE',
  lineHeight: 1.3,
  logoHeightMm: 14,
  qrSizeMm: 32,
  scale58: 85,
  sideMarginMm: 4,
};

const KEY = 'thermal_design_settings';

const clamp = (v: unknown, min: number, max: number, d: number) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.min(Math.max(n, min), max) : d;
};

export function normalizeThermalDesign(raw?: Partial<ThermalDesign> | null): ThermalDesign {
  const d = DEFAULT_THERMAL_DESIGN;
  const r = (raw || {}) as Partial<ThermalDesign>;
  const fields = {} as Record<ThermalFieldKey, ThermalField>;
  THERMAL_FIELDS.forEach(({ key, canHide }) => {
    const def = d.fields[key];
    const cur = (r.fields?.[key] || {}) as Partial<ThermalField>;
    fields[key] = {
      show: canHide ? cur.show !== undefined ? !!cur.show : def.show : true,
      size: clamp(cur.size, 6, 40, def.size),
      bold: cur.bold !== undefined ? !!cur.bold : def.bold,
    };
  });
  return {
    fields,
    titleText: typeof r.titleText === 'string' ? r.titleText : d.titleText,
    lineHeight: clamp(r.lineHeight, 1.1, 1.8, d.lineHeight),
    logoHeightMm: clamp(r.logoHeightMm, 5, 30, d.logoHeightMm),
    qrSizeMm: clamp(r.qrSizeMm, 15, 50, d.qrSizeMm),
    scale58: clamp(r.scale58, 60, 110, d.scale58),
    sideMarginMm: clamp(r.sideMarginMm, 0, 10, d.sideMarginMm),
  };
}

export function getThermalDesign(): ThermalDesign {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return normalizeThermalDesign(JSON.parse(raw));
  } catch {
    /* ignore */
  }
  return normalizeThermalDesign(null);
}

export function saveThermalDesign(d: ThermalDesign): boolean {
  try {
    localStorage.setItem(KEY, JSON.stringify(normalizeThermalDesign(d)));
    return true;
  } catch {
    return false;
  }
}
